import type { StockOpnameItem, StockReason } from "@artisancode/api-types";

export const OPNAME_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  year: "numeric",
};

/** "+Rp 1.000" / "-Rp 1.000" / "Rp 0". */
export function formatSignedRp(value: number): string {
  const rounded = Math.round(value);
  if (rounded === 0) return "Rp 0";
  const abs = Math.abs(rounded).toLocaleString("id-ID");
  return rounded < 0 ? `-Rp ${abs}` : `+Rp ${abs}`;
}

export function signedValueClass(value: number): string {
  const rounded = Math.round(value);
  if (rounded < 0) return "text-destructive";
  if (rounded > 0) return "text-emerald-600 dark:text-emerald-400";
  return "text-muted-foreground";
}

/** Discrepancy + reason state for one counted line. */
export function getOpnameItemState(
  item: StockOpnameItem,
  reasons: StockReason[],
  isRawMaterial: boolean,
) {
  const buckets = opnameBuckets(isRawMaterial, item.kind);
  const diffOnHand = buckets.onHand ? item.diffOnHand : 0;
  const diffRepacking = buckets.repacking ? item.diffRepacking : 0;
  const hasDiscrepancy = diffOnHand !== 0 || diffRepacking !== 0;
  const applicableReasons = getApplicableReasons(
    { diffOnHand, diffRepacking },
    reasons,
  );
  const isReasonValid = applicableReasons.some((r) => r.id === item.reasonId);
  // Hide a reason that no longer fits the current diff direction.
  const selectedReasonId = isReasonValid ? item.reasonId || "" : "";
  const isMissingReason = hasDiscrepancy && !isReasonValid;

  return {
    hasDiscrepancy,
    applicableReasons,
    selectedReasonId,
    isMissingReason,
  };
}

/** Counting rules per type and kind:
 * - products: on-hand only
 * - purchased raw materials: on-hand only (sealed warehouse packs)
 * - ingredient raw materials: repacking only (opened bar stock)
 * - direct raw materials: both on-hand and repacking
 */
export function isOpnameLineCounted(
  item: Pick<StockOpnameItem, "countedOnHand" | "countedRepacking" | "kind">,
  isRawMaterial: boolean,
): boolean {
  if (!isRawMaterial) {
    return item.countedOnHand !== null;
  }
  if (item.kind === "purchased") {
    return item.countedOnHand !== null;
  }
  if (item.kind === "ingredient") {
    return item.countedRepacking !== null;
  }
  return item.countedOnHand !== null && item.countedRepacking !== null;
}

/** Prepends the opened-pack calculator breakdown to an existing note. */
export function mergeCalculatorNote(
  calcNote: string,
  currentNote?: string | null,
): string {
  return currentNote ? `${calcNote} | ${currentNote}` : calcNote;
}

export function getApplicableReasons(
  item: { diffOnHand: number; diffRepacking: number },
  reasons: StockReason[],
): StockReason[] {
  const activeReasons = reasons.filter((r) => r.isActive !== false);
  const hasPos = item.diffOnHand > 0 || item.diffRepacking > 0;
  const hasNeg = item.diffOnHand < 0 || item.diffRepacking < 0;

  if (hasPos && hasNeg) {
    return activeReasons.filter((r) => r.direction === "both");
  }
  if (hasPos) {
    return activeReasons.filter(
      (r) => r.direction === "plus" || r.direction === "both",
    );
  }
  if (hasNeg) {
    return activeReasons.filter(
      (r) => r.direction === "minus" || r.direction === "both",
    );
  }
  return activeReasons;
}

/** Which buckets an opname line counts: purchased = warehouse only, ingredient = bar only. */
export function opnameBuckets(
  isRawMaterial: boolean,
  kind: StockOpnameItem["kind"],
) {
  return {
    onHand: !isRawMaterial || kind !== "ingredient",
    repacking: isRawMaterial && kind !== "purchased",
  };
}
